// Shared client-side payment proof uploader.
// Compresses large phone photos before upload so weak connections succeed
// reliably (a 5-15 MB photo becomes ~300 KB), then posts to the right
// backend endpoint based on the caller's role.
//
// Returns: { url, hash } on success.
// Throws : Error whose .message is the backend's "detail" if available.
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_DIMENSION = 1600;       // px — long edge after downscale
const TARGET_QUALITY = 0.85;      // JPEG quality
const HARD_LIMIT_BYTES = 8 * 1024 * 1024; // 8 MB safety net (server allows 10 MB)

/** Convert a File/Blob to a compressed JPEG File. PDFs and small images pass through. */
async function compressImage(file) {
  if (!file || !file.type) return file;
  // Skip non-image (e.g. PDF) and tiny images
  if (!file.type.startsWith("image/")) return file;
  if (file.size < 400 * 1024) return file;
  // Some image types (heic, webp on old browsers) may fail to decode — fall back to original
  try {
    const bitmap = await createImageBitmap(file).catch(async () => {
      // Fallback for browsers without createImageBitmap on HEIC etc.
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = dataUrl; });
      return img;
    });
    const w = bitmap.width || bitmap.naturalWidth;
    const h = bitmap.height || bitmap.naturalHeight;
    if (!w || !h) return file;
    const ratio = Math.min(1, MAX_DIMENSION / Math.max(w, h));
    const cw = Math.round(w * ratio);
    const ch = Math.round(h * ratio);
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, cw, ch);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", TARGET_QUALITY));
    if (!blob) return file;
    // If somehow compression made it larger, keep the original
    if (blob.size >= file.size) return file;
    return new File([blob], (file.name || "proof").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch (e) {
    if (process.env.NODE_ENV === "development") console.debug("[uploadProof] compress failed, using original:", e?.message);
    return file;
  }
}

/**
 * Upload a payment proof.
 * @param {File} file - the user-selected file
 * @param {Object} opts
 * @param {"client"|"affiliate"} opts.role - which endpoint to use
 * @param {string} opts.tokenKey - localStorage key holding the bearer token
 * @returns {Promise<{url:string, hash:string}>}
 */
export async function uploadPaymentProof(file, { role = "client", tokenKey = "client_token" } = {}) {
  if (!file) throw new Error("Aucun fichier sélectionné");
  // Pre-flight size guard so we never waste a 9 MB upload that the server will reject.
  if (file.size > HARD_LIMIT_BYTES * 2) {
    throw new Error("L'image est trop grande. Choisissez une capture d'écran récente ou réessayez.");
  }
  const compressed = await compressImage(file);
  if (compressed.size > HARD_LIMIT_BYTES) {
    throw new Error("L'image est trop grande même après compression. Essayez une capture d'écran plus petite.");
  }
  const endpoint = role === "client" ? "/client/upload-payment-proof" : "/affiliate/upload-payment-proof";
  const fd = new FormData();
  fd.append("file", compressed);
  try {
    const res = await axios.post(`${API}${endpoint}`, fd, {
      headers: { Authorization: `Bearer ${localStorage.getItem(tokenKey) || ""}` },
      // Do NOT manually set Content-Type — axios + FormData set the proper multipart boundary.
      timeout: 45000,
    });
    return res.data;
  } catch (e) {
    const detail = e?.response?.data?.detail;
    const msg = typeof detail === "string" && detail.trim()
      ? detail
      : (e?.message === "Network Error"
          ? "Connexion instable. Vérifiez votre internet et réessayez."
          : "Erreur lors du téléversement. Veuillez réessayer.");
    const err = new Error(msg);
    err.original = e;
    throw err;
  }
}
